/**
 * Session segmentation config: fetch which skin concerns are allowed (key === "yes")
 * so we can generate masks only for those and skip the rest.
 */
import { SESSION_APP_DATA_BASE } from '../config/sessionAppData';

export const SEGMENTATION_API_BASE = SESSION_APP_DATA_BASE;

export function parseFeatureToggle(value) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    return v === 'yes' || v === 'true' || v === '1' || v === 'on';
  }
  return false;
}

export function toYesNoValue(value) {
  if (value == null) return null;
  return parseFeatureToggle(value) ? 'yes' : 'no';
}

/**
 * Parse segmentation API payload (same rules as SkinRoutinePage).
 */
export function parseSegmentationPayload(data) {
  const fields = Array.isArray(data?.fields) ? data.fields : [];
  const segmentationMap = data?.segmentation && typeof data.segmentation === 'object'
    ? data.segmentation
    : null;

  let concernsEnabled = segmentationMap?.['Skin Concerns'] != null
    ? parseFeatureToggle(segmentationMap['Skin Concerns'])
    : true;
  let conditionsEnabled = segmentationMap?.['Skin Conditions'] != null
    ? parseFeatureToggle(segmentationMap['Skin Conditions'])
    : true;
  let advancedCharts = segmentationMap?.['Advanced graphical charts'] != null
    ? parseFeatureToggle(segmentationMap['Advanced graphical charts'])
    : true;

  const allowed = new Set();
  fields.forEach((field) => {
    const label = String(field?.label || '').trim();
    if (label === 'Skin Concerns' && segmentationMap?.['Skin Concerns'] == null) {
      concernsEnabled = parseFeatureToggle(field?.key);
    }
    if (label === 'Skin Conditions' && segmentationMap?.['Skin Conditions'] == null) {
      conditionsEnabled = parseFeatureToggle(field?.key);
    }
    if (label === 'Advanced graphical charts' && segmentationMap?.['Advanced graphical charts'] == null) {
      advancedCharts = parseFeatureToggle(field?.key);
    }
    if (field?.key !== 'yes' || !Array.isArray(field?.options)) return;
    field.options.forEach((opt) => {
      const name = normalizeSegmentationOption(opt);
      if (name) allowed.add(name);
    });
  });

  const hasProductFlag = Object.prototype.hasOwnProperty.call(data || {}, 'product_suggestion')
    || Object.prototype.hasOwnProperty.call(data || {}, 'product');
  const productSuggestion = hasProductFlag
    ? parseFeatureToggle(data?.product_suggestion ?? data?.product)
    : true;
  const productActions = resolveSegmentationProductActions(data);

  const productRecommendationSource =
    data?.product_recommendation_source === 'products_page' ? 'products_page' : 'database';

  const productSuggestLimit = null;

  const mode = String(data?.ai_mode || '').trim().toLowerCase();
  const aiMode = mode === 'advanced' || mode === 'basic' ? mode : null;
  const productsPageUrl = String(data?.products_page_url || '').trim();
  const productsPageUrls = Array.isArray(data?.products_page_urls)
    ? data.products_page_urls.map((u) => String(u || '').trim()).filter(Boolean)
    : (productsPageUrl ? [productsPageUrl] : []);
  const primaryProductUrl = String(data?.primary_product_url || '').trim();
  const primaryProductUrls = Array.isArray(data?.primary_product_urls)
    ? data.primary_product_urls.map((u) => String(u || '').trim()).filter(Boolean)
    : (primaryProductUrl ? [primaryProductUrl] : []);

  // Prefer answers lists when fields are empty (skin analysis settings overlay)
  if (allowed.size === 0 && data?.answers && typeof data.answers === 'object') {
    const pushOpts = (arr) => {
      if (!Array.isArray(arr)) return;
      arr.forEach((opt) => {
        const name = normalizeSegmentationOption(opt);
        if (name) allowed.add(name);
      });
    };
    if (concernsEnabled) pushOpts(data.answers['Skin Concerns']);
    if (conditionsEnabled) pushOpts(data.answers['Skin Conditions']);
  }

  return {
    showConcerns: concernsEnabled,
    showConditions: conditionsEnabled,
    advancedCharts,
    productSuggestion,
    addToCart: productActions.addToCart,
    productLink: productActions.productLink,
    allowedNames: allowed.size > 0 ? allowed : (fields.length > 0 ? new Set() : null),
    aiGeneratedSummary: toYesNoValue(data?.ai_generated_summary),
    aiMode,
    productSuggestLimit,
    productRecommendationSource,
    productsPageUrl: productsPageUrls[0] || productsPageUrl,
    productsPageUrls,
    primaryProductUrls,
    primaryProductUrl: primaryProductUrls[0] || primaryProductUrl,
  };
}

function isUrlLike(value) {
  if (typeof value !== 'string') return false;
  const v = value.trim();
  return /^https?:\/\//i.test(v) || v.startsWith('www.');
}

function readToggleFlag(sources, key) {
  for (const src of sources) {
    if (!src || typeof src !== 'object' || !Object.prototype.hasOwnProperty.call(src, key)) continue;
    const value = src[key];
    if (value == null || value === '') continue;
    if (isUrlLike(value)) continue;
    return parseFeatureToggle(value);
  }
  return false;
}

/** Product action flags from skin-analysis / segmentation (`add_to_cart`, `product_link`). */
export function resolveSegmentationProductActions(data) {
  const src = data && typeof data === 'object' ? data : {};
  const nested =
    (src.suggest_product && typeof src.suggest_product === 'object' && src.suggest_product)
    || (src.product_suggest && typeof src.product_suggest === 'object' && src.product_suggest)
    || {};
  const sources = [nested, src];
  return {
    addToCart: readToggleFlag(sources, 'add_to_cart'),
    productLink: readToggleFlag(sources, 'product_link'),
  };
}

export function resolveProductHref(product) {
  if (!product || typeof product !== 'object') return '';
  const candidates = [
    product.product_link,
    product.link,
    product.checkout_url,
    product.pdp_url,
    product.url,
    product.permalink,
    product.organization_domain_address,
  ];
  for (const value of candidates) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

export function isValidProductUrl(url) {
  if (url == null || typeof url !== 'string') return false;
  const trimmed = url.trim();
  return trimmed.length > 0;
}

/**
 * Map API option text to our class_name / displayName (must match backend and SkinResultsPage).
 */
export function normalizeSegmentationOption(option) {
  if (!option || typeof option !== 'string') return null;
  const s = option.trim();
  if (s.toLowerCase().startsWith('uneven') || s.toLowerCase().includes('uneven skin') || s.toLowerCase().includes('uneven tone')) return 'UnevenSkin';
  if (s.toLowerCase().startsWith('hydration')) return 'Hydration';
  if (s.toLowerCase().includes('smoothness')) return 'Smoothness';
  if (s.toLowerCase().startsWith('oxygen')) return 'Oxygen';
  if (s.toLowerCase().startsWith('dullness')) return 'Dullness';
  if (s === 'Wrinkles') return 'Wrinkles';
  if (s === 'Dry skin') return 'Dry skin';
  if (s === 'Oily skin') return 'Oily skin';
  if (s === 'Dark circle') return 'Dark circle';
  if (s === 'Acne') return 'Acne';
  if (s === 'Red spot') return 'Red spot';
  if (s === 'Pigmentation') return 'Pigmentation';
  if (s === 'Mole') return 'Mole';
  if (s === 'Dark spots') return 'Dark spots';
  if (s === 'Whiteheads') return 'Whiteheads';
  if (s === "Crow's feet") return "Crow's feet";
  if (s === 'Open pores') return 'Open Pores';
  if (s === 'Blackheads') return 'Blackheads';
  if (s === 'Clogged pores') return 'Clogged Pores';
  if (s === 'Eye Bags') return 'Eye Bags';
  if (s === 'Unknown') return 'Unknown';
  return s;
}

/**
 * Fetch segmentation config for session and return set of allowed condition names (key === "yes" only).
 * When key is "no" (e.g. "Skin Concerns" with options []), nothing is added to allowed, so we return
 * an empty set and the backend will skip all those concerns.
 * Returns null only if no token, request fails, or response has no fields array.
 * Returns empty Set when segmentation exists but no field has key "yes" (so backend must skip all).
 */
export async function fetchAllowedConditionNames(token) {
  if (!token) return null;
  try {
    const { fetchSkinAnalysisBundle } = await import('./skinAnalysisBundle');
    const data = await fetchSkinAnalysisBundle(token);
    if (!data) return null;
    const parsed = parseSegmentationPayload(data);
    if (parsed.allowedNames instanceof Set) return parsed.allowedNames;
    const fields = data?.fields ?? data?.data?.fields;
    if (!Array.isArray(fields)) return null;
    const allowed = new Set();
    fields.forEach((field) => {
      if (field.key !== 'yes') return;
      const opts = field.options;
      if (!Array.isArray(opts)) return;
      opts.forEach((opt) => {
        const name = normalizeSegmentationOption(opt);
        if (name) allowed.add(name);
      });
    });
    return allowed;
  } catch {
    return null;
  }
}
